/**
 * React hook for connecting to Atoll's shute WebSocket.
 * Handles connection lifecycle, keepalive ping/pong, session handshake,
 * and message dispatch to Redux.
 *
 * Renamed from services/websocket.tsx — follows React hook naming convention
 * (use prefix) and clarifies this is Atoll-specific shute protocol.
 */
import { useEffect } from 'react';
import { getShuteURL } from '../init';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { useAppDispatch } from "../../services/redux/hooks";
import { addMessage, setConnected, setSessionID, setVersion, clearQueuedEventListeners, clearPendingEventListeners, setCurrentEventListeners } from "../redux/shuteSlice";

export type ShuteOpenCallback = () => void;

/** Per-tab unique identifier for the shute WebSocket connection.
 *  Stored in sessionStorage so it persists across reconnects within the
 *  same tab but differs between tabs. The server uses this to allow
 *  multiple concurrent sockets from the same browser session. */
/** crypto.randomUUID() is only defined in secure contexts (HTTPS or
 *  localhost) — plain-HTTP LAN hosts like forge.lan need a fallback or
 *  every fresh tab crashes the app before the WS connection is made. */
function generateSqux(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

function getTabSqux(): string {
    let squx = sessionStorage.getItem('shute_squx');
    if (!squx) {
        squx = generateSqux();
        sessionStorage.setItem('shute_squx', squx);
    }
    return squx;
}

export const useShute = (isOffline: boolean, onOpen?: ShuteOpenCallback) => {
    const dispatch = useAppDispatch();

    useEffect(() => {
		if (isOffline) return; // Do not proceed if offline
        const shutePath = getShuteURL();
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const baseUrl = shutePath.startsWith('ws') ? shutePath : `${protocol}//${window.location.host}${shutePath}`;
        const separator = baseUrl.includes('?') ? '&' : '?';
        const wsUrl = `${baseUrl}${separator}squx=${getTabSqux()}`;
        const rws = new ReconnectingWebSocket(wsUrl);


        rws.addEventListener('open', () => {
            // Clear stale session state from previous connection — session ID
            // will be re-set when atoll.version event arrives
            dispatch(setSessionID(''));
            dispatch(setCurrentEventListeners([]));
            dispatch(clearPendingEventListeners());
            dispatch(clearQueuedEventListeners());
            dispatch(setConnected(true));
            // Send a pong back to the server
            rws.send(JSON.stringify({"eventname":"shute.pong","data":{}}));
            // Notify consumers that connection is fresh (e.g. to clear stale watch state)
            if (onOpen) onOpen();
        });

        rws.addEventListener('message', (message) => {
            const data = JSON.parse(message.data);
            var eventName = data.eventname;
            if (eventName === 'shute.ping') {
                const responseData = {
                    ...data,
                    eventname: "shute.pong",
                };

                // Send this new object as a string
                rws.send(JSON.stringify(responseData));
                dispatch(setConnected(true));
            } else if (eventName === 'atoll.version') {
                dispatch(setVersion(data.data?.version));
                dispatch(setSessionID(data.data?.sessionid));
            }
            dispatch(addMessage({event:eventName,data:data.data}));

        });

        rws.addEventListener('close', (event) => {
            console.log('disconnected');
            dispatch(setConnected(false));
        });
        rws.addEventListener('error', (event) => {
            console.log('ws error',event);
        });

        // When the component unmounts, we should close the websocket connection
        return () => {
            rws.close();
        };
    }, [dispatch, isOffline]);
};
